import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
} from "@mui/material";
import { useAuditLog } from "../../hooks/useAdminData";
import { useUserRole } from "../../hooks/useUserRole";
import { formatFullTimestamp } from "../../utils/timestampUtils";
import { formatAuditDetails } from "../../utils/auditLogUtils";

const ROWS_PER_PAGE = 10;

const AuditLogTab: React.FC = () => {
  const { role } = useUserRole();
  const [page, setPage] = useState(0);
  const { data, isLoading, error } = useAuditLog(ROWS_PER_PAGE, page);

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Backend never sends clerk_user_id in details; this is a safety net
  const sanitizeDetails = (details: Record<string, any> | null | undefined) => {
    if (!details) return null;
    const clone: Record<string, any> = { ...details };
    if ("clerk_user_id" in clone) delete clone.clerk_user_id;
    return clone;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} sx={{ color: "#EF2D3E" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load audit log. Check your permissions.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.875rem", color: "#111111" }}>
          Audit Log
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {role === "super_admin"
            ? "Showing recent admin actions across all users."
            : "Showing recent actions performed by admins (including yourself)."}
        </Typography>
      </Box>

      {entries.length === 0 && total === 0 ? (
        <Typography color="text.secondary">No audit entries yet.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatFullTimestamp(e.created_at)}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                      {e.actor_label ??
                        (e.actor_first_name || e.actor_last_name
                          ? `${e.actor_first_name || ""} ${e.actor_last_name || ""}`.trim()
                          : (e.actor_email ?? "Unknown user"))}
                    </Typography>
                    {e.actor_email && (
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        {e.actor_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.actor_role.replace("_", " ")}
                      sx={{ fontSize: "0.7rem", textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {e.action}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {e.target_label ?? (e.target_type || "—")}
                      {e.target_type &&
                      e.target_type !== "user" &&
                      e.target_id != null
                        ? ` / ${e.target_id}`
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        wordBreak: "break-word",
                      }}
                    >
                      {formatAuditDetails(e.action, sanitizeDetails(e.details), {
                        targetLabel: e.target_label ?? undefined,
                      })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            showFirstButton
            showLastButton
          />
        </Paper>
      )}
    </Box>
  );
};

export default AuditLogTab;
